"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Amy chat (T14): streaming conversation with the bitebalance-agent
 * AgentCore Runtime via the FAST agentcore-client (access token — the
 * runtime validates it and Amy's tools forward it to the REST API, so
 * identity flows end-to-end from this session's JWT; the REST surface
 * itself uses the ID token, see useApiClient.ts).
 *
 * Kept deliberately lean for the MVP: streamed text + a "checking your
 * data" indicator while tools run. Approval stays conversational — Amy
 * only ever calls apply after the user says yes in chat, and the
 * server-side confirmation-token flow means nothing can be applied that
 * wasn't first proposed and validated. Structured tool-cards reusing the
 * T12 diff component are a T20 polish item.
 *
 * F4 — mic button (Web Speech API, de-DE/en-US from the active locale).
 * Push-to-toggle; interim transcripts land in the input for editing and
 * are NEVER auto-sent. Hidden entirely where SpeechRecognition doesn't
 * exist (e.g. Firefox); a denied mic permission shows a friendly hint.
 *
 * F5 — photo attachments. Client downscales/compresses (amyImage.ts),
 * shows a removable thumbnail, sends [{format,base64}] alongside the
 * prompt, and renders the photo in the user's bubble via an object URL
 * (ephemeral by design — history restore shows a "[photo]" placeholder).
 */

import { useCallback, useEffect, useRef, useState } from "react"
import {
  Check,
  Copy,
  Mic,
  Paperclip,
  Pencil,
  X,
  Sparkles,
  UtensilsCrossed,
  PiggyBank,
  ChefHat,
} from "lucide-react"
import type { ProfileResponse } from "@bitebalance/contracts"
import { AgentCoreClient, generateTurnId, type AgentPattern } from "@/lib/agentcore-client"
import { amySessionFor, browserSessionStore, rotateRuntimeSession } from "@/lib/amySession"
import {
  browserConversationCache,
  durableConversationId,
  type AmyConversationIdentity,
} from "@/lib/amyConversation"
import {
  bubbleAfterFailure,
  emptyAttemptProgress,
  failureKind,
  progressAfterEvent,
  type AttemptProgress,
  type FailureKind,
} from "@/lib/amyInterruption"
import { useApiClient } from "@/api/useApiClient"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { noteAmyToolResult, notifyAmyDataChanged } from "@/lib/amySync"
import { CLIENT_BUILD_ID } from "@/lib/buildInfo"
import { clearAmyDraft, noteAmyDraft } from "@/lib/amyDraft"
import { takePendingDraft } from "@/lib/amyPrefillHandoff"
import {
  applyStreamEvent,
  dropEmptyAssistantByTurn,
  isAnsweredWhileAway,
  mergeRestoredHistory,
  threadWithPhaseBubble,
  updateAssistantByTurn,
} from "@/lib/amyTurn"
import { amyTurnStore, useAmyTurnState } from "@/lib/amyTurnStore"
import { browserTurnStore } from "@/lib/browserTurnStore"
import { AmyMarkdown } from "./AmyMarkdown"
import { processImageFile, type ProcessedImage } from "./amyImage"
import { Button } from "@/components/ui/button"

interface ChatMessage {
  role: "user" | "assistant"
  content: string
  /** True while at least one tool call is in flight for this message. */
  toolActive?: boolean
  /** Object URL of an attached photo (user messages only, this session only). */
  imageUrl?: string
  /** ADR-079: this assistant message landed while the user was away (marked by the server's clock). */
  answeredWhileAway?: boolean
  /** F12: the turn this message belongs to (this session's optimistic pair only). Every in-flight write finds its bubble by it, never by position. */
  turnId?: string
}

interface AgentConfig {
  agentRuntimeArn?: string
  /** deploy-frontend.py writes `awsRegion`; accept legacy `region` too. */
  awsRegion?: string
  region?: string
  agentPattern?: string
}

interface AmyChatProps {
  /** ADR-027: inserted into the composer once on mount, never auto-sent — see lib/amyLauncher.ts. */
  initialPrompt?: string | null
}

/** Draft first, prefill below it (one blank line apart); either alone as-is. Never auto-sent. */
export function composeInitialInput(draft: string | null, prompt: string | null): string {
  const kept = draft?.trim() === "" ? null : draft
  if (!kept) return prompt ?? ""
  if (!prompt) return kept
  return `${kept.trimEnd()}\n\n${prompt}`
}

export function AmyChat({ initialPrompt }: AmyChatProps = {}) {
  const { t, locale } = useI18n()
  const api = useApiClient()
  // #100: deployed-Amy-version cache (60s) for the pre-send handshake.
  // Failure degrades to the last known value (or null) — never blocks a send.
  const amyVersionRef = useRef<{ value: string | null; fetchedAt: number }>({
    value: null,
    fetchedAt: 0,
  })
  const currentAmyVersion = async (): Promise<string | null> => {
    const now = Date.now()
    if (now - amyVersionRef.current.fetchedAt < 60_000) return amyVersionRef.current.value
    try {
      const res = await api.getAmyVersion()
      amyVersionRef.current = { value: res.behaviorVersion, fetchedAt: now }
    } catch {
      amyVersionRef.current = { ...amyVersionRef.current, fetchedAt: now }
    }
    return amyVersionRef.current.value
  }
  const { getAccessToken, email } = useAuth()
  const [client, setClient] = useState<AgentCoreClient | null>(null)
  const [configError, setConfigError] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  // Fresh mount each time the drawer opens ({open && <AmyChat/>} in
  // AmyDrawer.tsx), so a lazy initializer is enough — never auto-sent, the
  // user still reviews/edits/hits send themselves (same principle as
  // example prompts and voice dictation below).
  //
  // Acceptance 4 (2026-09-04): a draft stashed by the shell's own
  // reload-before-prefill (lib/amyPrefillHandoff.ts) is restored first; a
  // prefill that arrives with it is appended below so nothing typed is lost.
  const [input, setInput] = useState(() =>
    composeInitialInput(takePendingDraft(), initialPrompt ?? null)
  )
  // ADR-079: the turn's phase comes from the durable-turn store, never from a socket. Send stays
  // disabled while the server may still be running the turn (a runtime session serves one turn at a time).
  const turnState = useAmyTurnState()
  const phase = turnState.phase
  const busy =
    phase.kind === "live" || phase.kind === "reconnecting" || phase.kind === "stillWorking"
  // The store's callbacks read the latest client without re-subscribing per render.
  const apiRef = useRef(api)
  useEffect(() => {
    apiRef.current = api
  }, [api])
  // ADR-058 decision 5: how the last send ended — "failed" (nothing came
  // back, generic error copy under the composer) or "interrupted" (the
  // bubble keeps the partial reply and carries the notice itself).
  const [sendFailure, setSendFailure] = useState<FailureKind | null>(null)
  const [pendingImage, setPendingImage] = useState<ProcessedImage | null>(null)
  const [photoError, setPhotoError] = useState<"tooLarge" | "readError" | null>(null)
  const [listening, setListening] = useState(false)
  const [voiceHint, setVoiceHint] = useState<"denied" | "error" | null>(null)
  // Greeting/history restore (2026-08 UX batch, items 7/8): first name for
  // the personalized "Hi {{name}}!" and the restored transcript, loaded
  // once on mount via plain REST (independent of the AgentCore streaming
  // client above — GET /v1/profile and GET /v1/amy/history).
  const [firstName, setFirstName] = useState<string | null>(null)
  const [historyStatus, setHistoryStatus] = useState<"loading" | "ready">("loading")
  const [historyDegraded, setHistoryDegraded] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const inputElRef = useRef<HTMLTextAreaElement | null>(null)
  // Owner report 2026-08-31 (parity with mobile): the single-line input
  // scrolled long messages away horizontally. The textarea auto-grows to
  // ~5 lines, then scrolls; Enter sends, Shift+Enter inserts a newline.
  const autoGrowInput = () => {
    const el = inputElRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`
  }
  // Height must also shrink back when send() clears the input
  // programmatically (onChange doesn't fire for setInput("")).
  useEffect(() => {
    autoGrowInput()
  }, [input])
  // A CTA can fire while the drawer is ALREADY open (the sheet is non-modal
  // and stays mounted) — before acceptance 4 the lazy initializer above was
  // the only reader, so a later prefill silently never reached the composer.
  // A new prompt is appended below any unsent text; never auto-sent.
  const appliedPromptRef = useRef<string | null>(initialPrompt ?? null)
  useEffect(() => {
    if (!initialPrompt) {
      appliedPromptRef.current = null
      return
    }
    if (initialPrompt === appliedPromptRef.current) return
    appliedPromptRef.current = initialPrompt
    setInput(prev => composeInitialInput(prev, initialPrompt))
    inputElRef.current?.focus()
  }, [initialPrompt])
  // Mirror the unsent text for the shell (reload-before-prefill stashes it).
  useEffect(() => {
    noteAmyDraft(input)
  }, [input])
  useEffect(() => () => clearAmyDraft(), [])
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  /** toolUseId → tool name, so tool_result events (which carry only the id) can be classified for the ADR-036 invalidation broadcast. */
  const toolNamesRef = useRef<Map<string, string>>(new Map())
  /** Owner 2026-08-29: copy affordance per assistant message (raw markdown → clipboard). */
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  /** Edit of a sent message (device pass 2026-10-10): APPEND-ONLY — the edited text is sent as a new
   * message at the END (no rewind), so the visible chat and the server's append-only history agree.
   * Explicit "edit" click only; banner + cancel (restores the prior draft); cleared on send, cancel,
   * chips/starters and history reloads. Mirror of mobile/src/app/amy.tsx. */
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const draftBeforeEditRef = useRef<string | null>(null)
  function startEdit(index: number, content: string) {
    if (draftBeforeEditRef.current === null) draftBeforeEditRef.current = input
    setEditingIndex(index)
    setInput(content)
    inputElRef.current?.focus()
  }
  function endEdit() {
    draftBeforeEditRef.current = null
    setEditingIndex(null)
  }
  function cancelEdit() {
    setInput(draftBeforeEditRef.current ?? "")
    endEdit()
  }
  async function copyMessage(index: number, content: string) {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedIndex(index)
      window.setTimeout(() => setCopiedIndex(current => (current === index ? null : current)), 1600)
    } catch {
      /* clipboard permission denied — selection still works as fallback */
    }
  }
  const dictationBaseRef = useRef("")

  // F4 feature detection: no SpeechRecognition (Firefox) → no button at all.
  const SpeechRecognitionImpl =
    typeof window === "undefined"
      ? undefined
      : (window.SpeechRecognition ?? window.webkitSpeechRecognition)

  useEffect(() => {
    let cancelled = false
    fetch("/aws-exports.json")
      .then(res => res.json())
      .then((config: AgentConfig) => {
        if (cancelled) return
        const region = config.awsRegion ?? config.region
        if (!config.agentRuntimeArn || !region) {
          setConfigError(true)
          return
        }
        setClient(
          new AgentCoreClient({
            runtimeArn: config.agentRuntimeArn,
            region,
            pattern: (config.agentPattern ?? "bitebalance-agent") as AgentPattern,
          })
        )
      })
      .catch(() => {
        if (!cancelled) setConfigError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Restore the recent transcript and load the caller's first name for the
  // greeting, once per mount (a drawer close/reopen remounts this
  // component, so this doubles as "reopen restores history" — item 8).
  // Independent try/catches: a profile hiccup must not block history, and
  // vice versa; a degraded/unavailable memory read still lets the user
  // chat, it just says so (AGENTS.md — never silently fabricate a "no
  // history" state as if it were confirmed).
  useEffect(() => {
    let cancelled = false
    api
      .getProfile()
      .then(profile => {
        if (cancelled) return
        setFirstName((profile as unknown as ProfileResponse).personalInfo?.firstName ?? null)
      })
      .catch(() => {
        /* no profile yet (brand-new user) — generic greeting is fine */
      })
    api
      .getAmyHistory()
      .then(history => {
        if (cancelled) return
        if (!history.available) setHistoryDegraded(true)
        if (history.messages.length > 0) {
          const restored: ChatMessage[] = history.messages.map(m => ({
            role: m.role,
            content: m.content,
          }))
          // F12: the user may have sent before this resolved — the restored history goes UNDER this
          // session's live messages (never replaces them), and the typed text stays in the composer.
          setMessages(prev => mergeRestoredHistory(restored, prev))
          draftBeforeEditRef.current = null
          setEditingIndex(null)
        }
      })
      .catch(() => {
        if (!cancelled) setHistoryDegraded(true)
      })
      .finally(() => {
        if (!cancelled) setHistoryStatus("ready")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  // ADR-079: the drawer remounts this chat on open — bind the pending-turn record and ask the server
  // about it on mount, on every return to the tab and on focus (a lost stream is recovered from
  // status, never guessed). Tab visibility is what "answered while you were away" is measured against.
  useEffect(() => {
    amyTurnStore.bindStorage(browserTurnStore(), email ?? "anon")
    const sync = () => {
      const visible = document.visibilityState === "visible"
      amyTurnStore.setScreenFocused(visible)
      if (visible) void amyTurnStore.resume(apiRef.current)
    }
    sync()
    document.addEventListener("visibilitychange", sync)
    window.addEventListener("focus", sync)
    return () => {
      document.removeEventListener("visibilitychange", sync)
      window.removeEventListener("focus", sync)
      amyTurnStore.setScreenFocused(false)
    }
  }, [email])
  // An answer that landed while the tab was away lives in Memory only: ONE history refetch once the tab
  // is visible again, the new assistant message marked by the server's own clock, the dashboards told to
  // refetch (a tool may have run). A refetch that fails leaves the answer pending for the next return.
  useEffect(() => {
    if (!turnState.awayAnswerPending || !turnState.screenFocused) return
    const answered = amyTurnStore.getState().phase
    if (answered.kind !== "answered") return
    let cancelled = false
    const newSince = answered.newSince
    apiRef.current
      .getAmyHistory()
      .then(history => {
        if (cancelled) return
        if (!history.available) {
          setHistoryDegraded(true)
          return
        }
        const restored: ChatMessage[] = history.messages.map(m => ({
          role: m.role,
          content: m.content,
          answeredWhileAway: isAnsweredWhileAway(m, newSince),
        }))
        // F12: every settled turn is already in the served history; only a turn still in flight (the
        // user sent again in the moment between "answered" and this refetch) stays on screen.
        const phaseNow = amyTurnStore.getState().phase
        const inFlight =
          phaseNow.kind === "live" ||
          phaseNow.kind === "reconnecting" ||
          phaseNow.kind === "stillWorking"
            ? new Set([phaseNow.turnId])
            : new Set<string>()
        setMessages(prev =>
          mergeRestoredHistory<ChatMessage>(restored, prev, { keepTurnIds: inFlight })
        )
        // A reload re-indexes the thread: an edit pointing into the old list must not survive it.
        draftBeforeEditRef.current = null
        setEditingIndex(null)
        amyTurnStore.ackAnswered()
        notifyAmyDataChanged()
      })
      .catch(() => {
        if (!cancelled) setHistoryDegraded(true)
      })
    return () => {
      cancelled = true
    }
  }, [turnState.awayAnswerPending, turnState.screenFocused])

  useEffect(() => {
    // Optional call: jsdom (vitest) implements no Element.scrollTo.
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight })
  }, [messages])

  // Stop dictation when the drawer unmounts mid-listen.
  useEffect(() => {
    return () => recognitionRef.current?.abort()
  }, [])

  const toggleVoice = useCallback(() => {
    if (!SpeechRecognitionImpl) return
    if (listening) {
      recognitionRef.current?.stop()
      setListening(false)
      return
    }
    const recognition = new SpeechRecognitionImpl()
    recognition.lang = locale === "de" ? "de-DE" : "en-US"
    recognition.interimResults = true
    recognition.continuous = true
    dictationBaseRef.current = input.trim() === "" ? "" : `${input.trimEnd()} `
    recognition.onresult = event => {
      let transcript = ""
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i]![0]?.transcript ?? ""
      }
      // Transcript lands in the input for the user to edit — never auto-sent.
      setInput(dictationBaseRef.current + transcript)
    }
    recognition.onerror = event => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setVoiceHint("denied")
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        setVoiceHint("error")
      }
      setListening(false)
    }
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    setVoiceHint(null)
    try {
      recognition.start()
      setListening(true)
    } catch {
      setVoiceHint("error")
    }
  }, [SpeechRecognitionImpl, listening, locale, input])

  const onFileSelected = useCallback(async (file: File | undefined) => {
    if (!file) return
    setPhotoError(null)
    const result = await processImageFile(file)
    if ("error" in result) {
      setPhotoError(result.error)
      return
    }
    setPendingImage(previous => {
      if (previous) URL.revokeObjectURL(previous.previewUrl)
      return result
    })
  }, [])

  const removePendingImage = useCallback(() => {
    setPendingImage(previous => {
      if (previous) URL.revokeObjectURL(previous.previewUrl)
      return null
    })
  }, [])

  const send = useCallback(async () => {
    const typed = input.trim()
    const image = pendingImage
    // A photo-only send gets a visible, honest default question — the user
    // sees exactly what is sent in their own bubble.
    const text = typed !== "" ? typed : image ? t("amy.photo.defaultPrompt") : ""
    if (!text || !client || busy) return
    recognitionRef.current?.abort()
    setListening(false)
    setInput("")
    setPendingImage(null)
    setPhotoError(null)
    setSendFailure(null)
    // ADR-079: the client mints the turn id BEFORE the request leaves — a stream that dies before its
    // first byte still leaves us an id to ask the server about; the same id rides the one retry.
    const turnId = generateTurnId()
    // Append-only (device pass 2026-10-10): an edited message is sent as a new message at the end.
    endEdit()
    setMessages(prev => [
      ...prev,
      { role: "user", content: text, imageUrl: image?.previewUrl, turnId },
      { role: "assistant", content: "", turnId },
    ])

    // F12: every write lands in THIS turn's own bubble (found by turnId), or nowhere.
    const updateAssistant = (updater: (m: ChatMessage) => ChatMessage) => {
      setMessages(prev => updateAssistantByTurn(prev, turnId, updater))
    }

    // One transparent retry on a FRESH session (deploy-killed sessions,
    // cold starts) before any user-visible error — backend work must be
    // invisible to users (owner mandate 2026-08-30) — but ONLY while nothing
    // came back (ADR-058 decision 5, below).
    //
    // What the CURRENT attempt streamed back, and which tools it started:
    // the failure classification (safe to retry vs. interrupted) reads the
    // former; the latter lets the dashboards behind the sheet refetch after
    // a tool that may have written before the stream broke.
    let progress: AttemptProgress = emptyAttemptProgress()
    const startedTools = new Set<string>()
    const attemptInvoke = async () => {
      progress = emptyAttemptProgress()
      const accessToken = await getAccessToken()
      // #100 version handshake: fetch the deployed Amy behavior version
      // (60s cache; failure → null, the send proceeds and the next read
      // rotates) and let the session module decide — a version change
      // rotates the RUNTIME session while conversationId keeps history.
      const version = await currentAmyVersion()
      // #106: the durable conversationId is SERVER-owned (cached locally
      // for speed). Cold cache + identity endpoint down fails the send
      // closed — a locally minted id would orphan the user's real thread.
      const identity: AmyConversationIdentity = await durableConversationId({
        fetchIdentity: () => api.getAmyConversation(),
        cache: browserConversationCache(),
        userKey: email ?? "anon",
      })
      const session = amySessionFor(
        browserSessionStore(),
        email ?? "anon",
        version,
        Date.now(),
        identity.conversationId
      )
      amyTurnStore.beginTurn({
        turnId,
        conversationId: session.conversationId,
        runtimeSessionId: session.runtimeSessionId,
        promptPreview: text.slice(0, 120),
        now: Date.now(),
      })
      // Identity is extracted server-side from the validated JWT — never a
      // chat/tool parameter (prompt injection cannot impersonate).
      await client.invoke(
        text,
        session.runtimeSessionId,
        accessToken,
        event => {
          progress = progressAfterEvent(progress, event.type)
          switch (event.type) {
            case "lifecycle":
              // ADR-079: the runtime's own account of THIS turn (started / completed with answerRecorded /
              // failed / abandoned / duplicate) drives the phase; text keeps streaming into the bubble.
              if (event.turnId !== turnId) break
              if (event.event === "turn_started")
                amyTurnStore.dispatch({ type: "turn_started", turnId })
              else if (event.event === "turn_completed")
                amyTurnStore.dispatch({
                  type: "turn_completed",
                  turnId,
                  answerRecorded: event.answerRecorded === true,
                  screenFocused: amyTurnStore.getState().screenFocused,
                  now: Date.now(),
                })
              else if (
                event.event === "turn_failed" ||
                event.event === "turn_abandoned" ||
                event.event === "turn_duplicate"
              )
                amyTurnStore.dispatch({ type: event.event, turnId })
              break
            case "text":
              setMessages(prev => applyStreamEvent(prev, turnId, event))
              break
            case "tool_use_start":
              toolNamesRef.current.set(event.toolUseId, event.name)
              startedTools.add(event.name)
              setMessages(prev => applyStreamEvent(prev, turnId, event))
              break
            case "tool_result": {
              // ADR-036 finding 6: a finished mutating tool means the
              // dashboards behind this sheet are stale — broadcast so they
              // refetch (amySync also schedules late refetches after a
              // regenerate_plan whose job outlived the tool's poll).
              const name = toolNamesRef.current.get(event.toolUseId)
              if (name) noteAmyToolResult(name)
              setMessages(prev => applyStreamEvent(prev, turnId, event))
              break
            }
            default:
              break
          }
        },
        {
          ...(image ? { images: [{ format: image.format, base64: image.base64 }] } : {}),
          conversationId: session.conversationId,
          client: "web",
          // ADR-057 FAILURE TRUTH: the UI language, so a reply produced before the model runs is in the user's language.
          locale,
          // Acceptance 4: the bundle sending this turn (AMY_TURN logs it).
          clientBuild: CLIENT_BUILD_ID,
          conversationSource: identity.source,
          ...(session.rebound
            ? {
                sessionRebound: true,
                clientBoundVersion: session.previousBoundVersion ?? undefined,
              }
            : {}),
          // ADR-079: the client-minted turn id (the runtime echoes it as the first event).
          turnId,
        }
      )
      // A settled stream is the answer for a legacy runtime; after a `turn_duplicate` the server's status settles it.
      await amyTurnStore.settleAfterStream(api, turnId)
    }

    try {
      try {
        await attemptInvoke()
      } catch (firstErr) {
        // ADR-079 (replaces ADR-058's "nothing came back ⇒ resend"): a lost stream is answered by the
        // SERVER's status for this turn. Anything it knows is resumed — still working, answered while
        // away, or an honest failure — because a turn the server may still be running must never run
        // twice (three orphan recipes, founder 2026-09-05). The one transparent retry stays, ONLY when
        // the server has never seen the turn AND nothing came back.
        const decision = await amyTurnStore.afterStreamLoss(api, { turnId, attempt: 1, progress })
        if (decision.action === "resume") {
          console.info("AMY_TURN_RESUME", turnId)
          for (const name of startedTools) noteAmyToolResult(name)
          return
        }
        if (decision.action !== "retry") throw firstErr
        console.error("AMY_INVOKE_RETRY", firstErr)
        rotateRuntimeSession(browserSessionStore(), email ?? "anon")
        await new Promise(resolve => setTimeout(resolve, 1200))
        updateAssistant(m => ({ ...m, content: "", toolActive: false }))
        try {
          await attemptInvoke()
        } catch (secondErr) {
          // The retry's stream died too: once more the server decides — it may well be running the turn.
          const second = await amyTurnStore.afterStreamLoss(api, { turnId, attempt: 2, progress })
          if (second.action === "resume") {
            console.info("AMY_TURN_RESUME", turnId)
            for (const name of startedTools) noteAmyToolResult(name)
            return
          }
          throw secondErr
        }
      }
    } catch (err) {
      const kind = failureKind(progress)
      console.error(kind === "interrupted" ? "AMY_INVOKE_INTERRUPTED" : "AMY_INVOKE_FAILED", err)
      // ADR-079: when the server never registered the turn, the composer notice quotes the message
      // (amy.turn.notReceived) — the empty bubble is dropped and no second, generic alert is shown.
      const after = amyTurnStore.getState().phase
      const notReceived = after.kind === "failed" && after.reason === "unregistered"
      setSendFailure(notReceived ? null : kind)
      // A tool that started before the stream broke may have written without
      // its tool_result ever arriving (ADR-036 finding 6) — refetch the
      // dashboards behind the sheet so "check Today and Plan" shows the truth.
      for (const name of startedTools) noteAmyToolResult(name)
      const copy = { interrupted: t("amy.interrupted"), error: t("amy.error") }
      if (notReceived) setMessages(prev => dropEmptyAssistantByTurn(prev, turnId))
      else
        updateAssistant(m => ({
          ...m,
          content: bubbleAfterFailure(m.content, progress, copy),
          toolActive: false,
        }))
    } finally {
      // ADR-079: `busy` derives from the turn store. A stream that settled without any terminal event
      // is closed here as a safety net (a no-op in every other phase).
      amyTurnStore.dispatch({ type: "stream_end", turnId })
    }
  }, [api, input, pendingImage, client, busy, getAccessToken, email, t, editingIndex])

  // Example prompts insert into the input for the user to review/edit —
  // never auto-sent, same principle as F4 dictation.
  const insertPrompt = useCallback((prompt: string) => {
    draftBeforeEditRef.current = null
    setEditingIndex(null)
    setInput(prompt)
    inputElRef.current?.focus()
  }, [])

  if (configError) {
    return <p className="px-4 pb-4 text-sm text-muted-foreground">{t("amy.error")}</p>
  }

  const showGreeting = historyStatus === "ready" && messages.length === 0
  // Amy-first pass 2026-08-29 (mobile parity): returning users get
  // time-aware quick chips above the composer — same amyCompanion copy as
  // the mobile companion card; inserted, never auto-sent.
  const chipHour = new Date().getHours()
  const chipSlot = chipHour < 11 ? "morning" : chipHour < 17 ? "day" : "evening"
  const quickChips = [
    t(`amyCompanion.${chipSlot}Chip1`),
    t(`amyCompanion.${chipSlot}Chip2`),
    t("amy.greeting.promptCheaper"),
  ]
  const showQuickChips = messages.length > 0 && !busy && input.trim() === ""
  // ADR-079: what the EMPTY last bubble says, by turn phase — calm "still working" while the server runs
  // the turn, an honest "couldn't finish" for a failure the server reported, "thinking" while the stream
  // is live. A bubble with content keeps its content (partial replies are never replaced).
  const phaseCopy =
    phase.kind === "stillWorking"
      ? t("amy.turn.stillWorking")
      : (phase.kind === "failed" && phase.reason !== "unregistered") || phase.kind === "abandoned"
        ? t("amy.turn.couldNotFinish")
        : busy
          ? t("amy.thinking")
          : null
  // After a reload or a reopened drawer the thread may end with the user's own question (the answer is not
  // in Memory yet): the phase copy then rides a synthetic empty assistant bubble, so a pending turn is never invisible.
  const thread = threadWithPhaseBubble(messages, phaseCopy !== null, () => ({
    role: "assistant" as const,
    content: "",
  }))
  const examplePrompts = [
    { icon: UtensilsCrossed, text: t("amy.greeting.promptAteOut") },
    { icon: PiggyBank, text: t("amy.greeting.promptCheaper") },
    { icon: ChefHat, text: t("amy.greeting.promptRecipe") },
  ]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4" aria-live="polite">
        {historyStatus === "loading" && (
          <p className="py-4 text-sm text-muted-foreground">{t("amy.historyLoading")}</p>
        )}
        {historyDegraded && (
          <p role="status" className="mt-2 rounded-bb-md bg-cream px-3 py-2 text-xs text-ink-2">
            {t("amy.historyUnavailable")}
          </p>
        )}
        {showGreeting && (
          <div className="my-3 flex flex-col gap-4 rounded-bb-xl border border-line bg-paper p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-green">
                <Sparkles className="size-5 text-lime" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-ink">
                  {firstName
                    ? t("amy.greeting.hiName", { name: firstName })
                    : t("amy.greeting.hiGeneric")}
                </h2>
                <p className="text-sm text-ink-2">{t("amy.greeting.subtitle")}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
                {t("amy.greeting.promptsLabel")}
              </p>
              {examplePrompts.map(({ icon: Icon, text }) => (
                <button
                  key={text}
                  type="button"
                  onClick={() => insertPrompt(text)}
                  className="flex min-h-11 items-center gap-3 rounded-bb-lg border border-line bg-cream px-3.5 py-2.5 text-left text-sm font-medium text-ink transition-colors hover:border-green/40 hover:bg-green/5"
                >
                  <Icon className="size-4 shrink-0 text-green" aria-hidden="true" />
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}
        <ul className="flex flex-col gap-3 py-2 select-text">
          {thread.map((message, index) => (
            <li
              // F12: a session message keeps its key when the restored history lands underneath it.
              key={message.turnId ? `${message.turnId}:${message.role}` : `i${index}`}
              className={
                message.role === "user"
                  ? "ml-8 self-end whitespace-pre-wrap rounded-bb-lg bg-green/10 px-3 py-2 text-sm text-ink"
                  : "mr-4 self-start rounded-bb-lg bg-cream px-3 py-2 text-sm text-ink"
              }
            >
              {message.imageUrl && (
                <img
                  src={message.imageUrl}
                  alt={t("amy.photo.bubbleAlt")}
                  className="mb-2 max-h-48 max-w-full rounded-bb-md"
                />
              )}
              {message.role === "assistant" && message.content ? (
                <AmyMarkdown content={message.content} />
              ) : (
                message.content || (index === thread.length - 1 && phaseCopy ? phaseCopy : "")
              )}
              {message.role === "assistant" && message.answeredWhileAway && (
                <span aria-live="polite" className="mt-1 block text-[11px] text-muted-foreground">
                  {t("amy.turn.answeredWhileAway")}
                </span>
              )}
              {message.toolActive && (
                <span role="status" className="mt-1 block text-xs text-muted-foreground">
                  {t("amy.toolRunning")}
                </span>
              )}
              {message.role === "user" && !busy && (
                <button
                  type="button"
                  aria-label={t("amy.editMessage")}
                  title={t("amy.editMessage")}
                  onClick={() => startEdit(index, message.content)}
                  className="mt-1 flex items-center gap-1 rounded-bb-sm text-[11px] font-medium text-muted-foreground transition-colors hover:text-ink"
                >
                  <Pencil className="size-3" aria-hidden="true" />
                  {t("amy.editMessage")}
                </button>
              )}
              {message.role === "assistant" && message.content && !message.toolActive && (
                <button
                  type="button"
                  aria-label={t("amy.copyMessage")}
                  title={t("amy.copyMessage")}
                  onClick={() => void copyMessage(index, message.content)}
                  className="mt-1.5 flex items-center gap-1 rounded-bb-sm text-[11px] font-medium text-muted-foreground transition-colors hover:text-ink"
                >
                  {copiedIndex === index ? (
                    <>
                      <Check className="size-3 text-green" aria-hidden="true" />
                      {t("amy.copiedMessage")}
                    </>
                  ) : (
                    <>
                      <Copy className="size-3" aria-hidden="true" />
                      {t("amy.copyMessage")}
                    </>
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {pendingImage && (
        <div className="flex items-center gap-2 border-t border-line px-4 py-2">
          <span className="relative inline-block">
            <img
              src={pendingImage.previewUrl}
              alt={t("amy.photo.previewAlt")}
              className="h-16 w-16 rounded-bb-md border border-line object-cover"
            />
            <button
              type="button"
              aria-label={t("amy.photo.remove")}
              onClick={removePendingImage}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-paper"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        </div>
      )}

      {showQuickChips && (
        <div className="flex gap-2 overflow-x-auto px-4 pb-2">
          {quickChips.map(chip => (
            <button
              key={chip}
              type="button"
              onClick={() => insertPrompt(chip)}
              className="shrink-0 rounded-bb-full border border-line bg-paper px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-green hover:bg-green/5"
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {editingIndex !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 border-t border-line bg-cream px-4 py-2 text-xs text-ink-2"
        >
          <span className="flex items-center gap-1.5">
            <Pencil className="size-3 shrink-0" aria-hidden="true" />
            {t("amy.editingNotice")}
          </span>
          <button
            type="button"
            onClick={cancelEdit}
            className="shrink-0 font-semibold text-ink underline-offset-2 hover:underline"
          >
            {t("amy.editCancel")}
          </button>
        </div>
      )}
      <form
        className="flex items-end gap-2 border-t border-line px-4 py-3"
        onSubmit={event => {
          event.preventDefault()
          void send()
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="amy-photo-input"
          onChange={event => {
            void onFileSelected(event.target.files?.[0])
            event.target.value = ""
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="min-h-11 px-3"
          aria-label={t("amy.photo.attach")}
          disabled={!client || busy}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="h-4 w-4" aria-hidden />
        </Button>
        <label htmlFor="amy-input" className="sr-only">
          {t("amy.inputLabel")}
        </label>
        <textarea
          id="amy-input"
          ref={inputElRef}
          rows={1}
          value={input}
          onChange={event => {
            setInput(event.target.value)
            autoGrowInput()
          }}
          onKeyDown={event => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              void send()
            }
          }}
          placeholder={listening ? t("amy.voice.listening") : t("amy.inputPlaceholder")}
          disabled={!client || busy}
          className="min-h-11 max-h-32 flex-1 resize-none rounded-bb-md border border-line bg-paper px-3 py-2.5 text-sm leading-5 text-ink"
        />
        {SpeechRecognitionImpl && (
          <Button
            type="button"
            variant={listening ? "default" : "outline"}
            className="min-h-11 px-3"
            aria-label={listening ? t("amy.voice.stop") : t("amy.voice.start")}
            aria-pressed={listening}
            disabled={!client || busy}
            onClick={toggleVoice}
          >
            <Mic className="h-4 w-4" aria-hidden />
          </Button>
        )}
        <Button
          type="submit"
          className="min-h-11"
          disabled={!client || busy || (input.trim() === "" && !pendingImage)}
        >
          {t("amy.send")}
        </Button>
      </form>
      {listening && (
        <p role="status" className="px-4 pb-2 text-xs text-muted-foreground">
          {t("amy.voice.listening")}
        </p>
      )}
      {voiceHint && (
        <p role="status" className="px-4 pb-2 text-xs text-muted-foreground">
          {voiceHint === "denied" ? t("amy.voice.permissionDenied") : t("amy.voice.error")}
        </p>
      )}
      {photoError && (
        <p role="alert" className="px-4 pb-2 text-xs text-destructive">
          {photoError === "tooLarge" ? t("amy.photo.tooLarge") : t("amy.photo.readError")}
        </p>
      )}
      {/* An interruption is explained inside the bubble it belongs to (the
          partial reply + amy.interrupted); only the nothing-came-back case
          keeps the generic alert here — a second copy of the notice under the
          composer would only duplicate it. */}
      {phase.kind === "failed" && phase.reason === "unregistered" ? (
        <p role="alert" className="px-4 pb-2 text-xs text-destructive">
          {t("amy.turn.notReceived")}
          {turnState.notReceivedPreview ? ` \u201C${turnState.notReceivedPreview}\u201D` : ""}
        </p>
      ) : (
        sendFailure === "failed" && (
          <p role="alert" className="px-4 pb-2 text-xs text-destructive">
            {t("amy.error")}
          </p>
        )
      )}
      <p className="px-4 pb-3 text-xs text-muted-foreground">{t("amy.disclaimer")}</p>
    </div>
  )
}
